/**
 * Control-plane wire contract (the `/admin` API used by the CMS SPA).
 *
 * Kept separate from `api.ts` on purpose: the data plane is the product other
 * developers integrate against and must stay stable, while the admin surface
 * can evolve with the CMS.
 */

import { z } from 'zod';
import {
  API_KEY_SCOPE,
  MODERATION_DECISION,
  PLATFORM_ROLE,
  TENANT_STATUS,
  TITLE_STATUS,
  TITLE_VISIBILITY,
} from './enums';
import { StatsDto, WalletDto } from './api';

// ---------------------------------------------------------------------------
// Tenants
// ---------------------------------------------------------------------------

export const TenantDto = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  status: z.enum(TENANT_STATUS),
  /** Tenant-scoped Bunny Stream library, so one tenant's videos are never served to another. */
  bunnyLibraryId: z.string().nullable(),
  bunnyPullZoneId: z.string().nullable(),
  defaultCurrency: z.string().length(3),
  settings: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});
export type Tenant = z.infer<typeof TenantDto>;

export const CreateTenantRequest = z.object({
  slug: z
    .string()
    .trim()
    .min(2)
    .max(40)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lowercase letters, digits and single hyphens only'),
  name: z.string().trim().min(2).max(120),
  defaultCurrency: z.string().trim().length(3).default('USD'),
  /** Provision a dedicated Bunny Stream library for this tenant. */
  provisionBunnyLibrary: z.boolean().default(true),
});
export type CreateTenant = z.infer<typeof CreateTenantRequest>;

export const UpdateTenantRequest = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  status: z.enum(TENANT_STATUS).optional(),
  defaultCurrency: z.string().trim().length(3).optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
});

export const ApiKeyDto = z.object({
  id: z.string(),
  name: z.string(),
  /** Masked, e.g. `dk_live_9f2…c41a`. The full secret is shown once, at creation. */
  prefix: z.string(),
  scopes: z.array(z.enum(API_KEY_SCOPE)),
  lastUsedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type ApiKey = z.infer<typeof ApiKeyDto>;

export const CreateApiKeyRequest = z.object({
  name: z.string().trim().min(2).max(80),
  scopes: z.array(z.enum(API_KEY_SCOPE)).min(1),
  expiresInDays: z.number().int().positive().max(3650).optional(),
});

/** Returned once. The plaintext secret is never stored or retrievable again. */
export const CreatedApiKeyDto = ApiKeyDto.extend({ secret: z.string() });
export type CreatedApiKey = z.infer<typeof CreatedApiKeyDto>;

// ---------------------------------------------------------------------------
// Content authoring
// ---------------------------------------------------------------------------

export const AdminEpisodeDto = z.object({
  id: z.string(),
  seasonId: z.string(),
  episodeNumber: z.number().int(),
  title: z.string(),
  durationSeconds: z.number().int().nonnegative(),
  isFree: z.boolean(),
  coinPrice: z.number().int().nonnegative(),
  bunnyVideoId: z.string().nullable(),
  transcodeStatus: z.enum(['none', 'queued', 'processing', 'ready', 'failed']),
  thumbnailUrl: z.string().nullable(),
  status: z.enum(TITLE_STATUS),
});
export type AdminEpisode = z.infer<typeof AdminEpisodeDto>;

export const AdminSeasonDto = z.object({
  id: z.string(),
  seasonNumber: z.number().int(),
  title: z.string(),
  episodes: z.array(AdminEpisodeDto),
});
export type AdminSeason = z.infer<typeof AdminSeasonDto>;

export const AdminTitleDto = z.object({
  id: z.string(),
  slug: z.string(),
  tenantId: z.string(),
  owningTenantName: z.string(),
  creatorId: z.string().nullable(),
  creatorName: z.string().nullable(),
  title: z.string(),
  subTitle: z.string().nullable(),
  synopsis: z.string(),
  genres: z.array(z.string()),
  tags: z.array(z.string()),
  cast: z.array(z.string()),
  director: z.string().nullable(),
  releaseYear: z.number().int().nullable(),
  status: z.enum(TITLE_STATUS),
  visibility: z.enum(TITLE_VISIBILITY),
  featured: z.boolean(),
  rating: z.number().min(0).max(10),
  themeColor: z.string().nullable(),
  posterVerticalUrl: z.string().nullable(),
  posterHorizontalUrl: z.string().nullable(),
  heroBackdropUrl: z.string().nullable(),
  /** Whether an embedding exists; drives the similarity quality indicator. */
  hasEmbedding: z.boolean(),
  embeddingModel: z.string().nullable(),
  stats: StatsDto,
  seasons: z.array(AdminSeasonDto),
  createdAt: z.string(),
  updatedAt: z.string(),
  publishedAt: z.string().nullable(),
});
export type AdminTitle = z.infer<typeof AdminTitleDto>;

export const UpsertTitleRequest = z.object({
  slug: z
    .string()
    .trim()
    .min(2)
    .max(120)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .optional(),
  title: z.string().trim().min(1).max(180),
  subTitle: z.string().trim().max(240).nullish(),
  synopsis: z.string().trim().min(1).max(5000),
  genres: z.array(z.string().trim().min(1).max(64)).max(12).default([]),
  tags: z.array(z.string().trim().min(1).max(64)).max(24).default([]),
  cast: z.array(z.string().trim().min(1).max(120)).max(40).default([]),
  director: z.string().trim().max(120).nullish(),
  releaseYear: z.number().int().min(1900).max(2100).nullish(),
  visibility: z.enum(TITLE_VISIBILITY).default('tenant'),
  featured: z.boolean().default(false),
  themeColor: z.string().trim().max(9).nullish(),
  creatorId: z.string().nullish(),
});
export type UpsertTitle = z.infer<typeof UpsertTitleRequest>;

export const UpsertSeasonRequest = z.object({
  seasonNumber: z.number().int().min(1).max(99),
  title: z.string().trim().min(1).max(180),
});

export const UpsertEpisodeRequest = z.object({
  episodeNumber: z.number().int().min(1).max(9999),
  title: z.string().trim().min(1).max(240),
  durationSeconds: z.number().int().nonnegative().default(0),
  isFree: z.boolean().default(false),
  coinPrice: z.number().int().nonnegative().default(5),
});

/** Bulk-create placeholders, e.g. "episodes 1..96" for a new season. */
export const BulkEpisodeRequest = z.object({
  seasonId: z.string(),
  fromEpisodeNumber: z.number().int().min(1),
  toEpisodeNumber: z.number().int().min(1),
  freeEpisodeCount: z.number().int().nonnegative().default(0),
  coinPrice: z.number().int().nonnegative().default(5),
}).refine((v) => v.toEpisodeNumber >= v.fromEpisodeNumber, {
  message: 'toEpisodeNumber must be >= fromEpisodeNumber',
});

/**
 * Short-lived ticket for uploading a video. Large files go straight from the
 * browser/CLI to Bunny — we never proxy video bytes through the API.
 */
export const UploadTicketDto = z.object({
  episodeId: z.string(),
  bunnyVideoId: z.string(),
  /** Bunny's per-video upload endpoint. PUT the file here. */
  uploadUrl: z.string(),
  uploadHeaders: z.record(z.string(), z.string()),
  expiresInSeconds: z.number().int().positive(),
});
export type UploadTicket = z.infer<typeof UploadTicketDto>;

// ---------------------------------------------------------------------------
// Moderation
// ---------------------------------------------------------------------------

export const ReviewQueueItemDto = z.object({
  title: AdminTitleDto,
  submittedBy: z.string().nullable(),
  submittedAt: z.string().nullable(),
  /** Uploader's copyright attestation — must be true before approval. */
  copyrightAttestation: z.boolean(),
  episodeCount: z.number().int().nonnegative(),
  transcodedEpisodeCount: z.number().int().nonnegative(),
  previousDecision: z.enum(MODERATION_DECISION).nullable(),
  previousNotes: z.string().nullable(),
});
export type ReviewQueueItem = z.infer<typeof ReviewQueueItemDto>;

export const ModerationDecisionRequest = z.object({
  decision: z.enum(MODERATION_DECISION),
  notes: z.string().trim().max(4000).default(''),
  /** Publish immediately on approval instead of leaving it in `approved`. */
  publishNow: z.boolean().default(false),
});

// ---------------------------------------------------------------------------
// Creators, plans, usage, audit
// ---------------------------------------------------------------------------

export const CreatorDto = z.object({
  id: z.string(),
  tenantId: z.string(),
  displayName: z.string(),
  email: z.string().nullable(),
  /** Reserved for Phase 2 payouts — stored now so it is not a migration later. */
  revenueSharePct: z.number().min(0).max(100),
  status: z.enum(['pending', 'active', 'blocked']),
  titleCount: z.number().int().nonnegative(),
  createdAt: z.string(),
});
export type Creator = z.infer<typeof CreatorDto>;

export const UpsertCreatorRequest = z.object({
  displayName: z.string().trim().min(2).max(120),
  email: z.string().trim().nullish(),
  revenueSharePct: z.number().min(0).max(100).default(0),
  status: z.enum(['pending', 'active', 'blocked']).default('pending'),
});

export const PlanDto = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  priceMinor: z.number().int().nonnegative(),
  currency: z.string().length(3),
  interval: z.enum(['month', 'year']),
  includedApiCalls: z.number().int().nonnegative(),
  includedStreamGb: z.number().int().nonnegative(),
  includedMonthlyActiveUsers: z.number().int().nonnegative(),
  /** Overage rates in minor units, per unit above. */
  overageApiCallsPer1kMinor: z.number().int().nonnegative(),
  overageStreamGbMinor: z.number().int().nonnegative(),
  stripePriceId: z.string().nullable(),
});
export type Plan = z.infer<typeof PlanDto>;

export const UsageDayDto = z.object({
  date: z.string(),
  apiCalls: z.number().int().nonnegative(),
  streamGb: z.number().min(0),
  activeUsers: z.number().int().nonnegative(),
  playbackRequests: z.number().int().nonnegative(),
});
export type UsageDay = z.infer<typeof UsageDayDto>;

export const AuditLogEntryDto = z.object({
  id: z.string(),
  actorId: z.string().nullable(),
  actorEmail: z.string().nullable(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  tenantId: z.string().nullable(),
  meta: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});
export type AuditLogEntry = z.infer<typeof AuditLogEntryDto>;

export const AdminSessionDto = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number().int(),
  user: z.object({
    id: z.string(),
    email: z.string(),
    displayName: z.string().nullable(),
    role: z.enum(PLATFORM_ROLE),
    /** Null for platform staff, who operate across tenants. */
    tenantId: z.string().nullable(),
  }),
});
export type AdminSession = z.infer<typeof AdminSessionDto>;

export const AdminWalletOverrideRequest = z.object({
  userId: z.string(),
  deltaCoins: z.number().int(),
  deltaGems: z.number().int().default(0),
  reason: z.string().trim().min(3).max(500),
  idempotencyKey: z.string().min(8).max(128),
});

export { WalletDto };
